/**
 * ContactActions — clickable/actionable contact elements used app-wide:
 *   EmailAction  → mailto link + envelope button that opens Claire's AI draft modal
 *   PhoneAction  → tel: link + "open in WhatsApp" (wa.me) shortcut
 *   ClaireEmailDraftModal → AI-written subject + opening message (1 credit)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Sparkles, Loader2, Copy, Send, MessageCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ClaireEmailDraftModal({ open, onClose, email, context, isPt: isPtProp }) {
  const [lang, setLang] = useState(isPtProp ? "pt" : "en");
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const generate = useCallback(async (lng) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/email-draft/generate`, {
        lang: lng, to_email: email, context: context || {},
      });
      setSubject(data.subject || "");
      setBody(data.body || "");
    } catch (e) {
      const msg = e?.response?.status === 402
        ? (lng === "pt" ? "Créditos insuficientes — recarregue em Billing" : "Insufficient credits — top up in Billing")
        : (e?.response?.data?.detail || "Claire draft failed");
      toast.error(msg);
    } finally { setLoading(false); }
  }, [email, context]);

  useEffect(() => {
    if (open) { setSubject(""); setBody(""); generate(lang); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const isPt = lang === "pt";
  const mailtoHref = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      data-testid="claire-draft-modal" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="w-full max-w-lg bg-[var(--al-card-bg,#fff)] text-[var(--al-text,#111827)] rounded-2xl shadow-2xl border border-[var(--al-border,#E5E7EB)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold">{isPt ? "Claire — Rascunho de E-mail" : "Claire — Email Draft"}</div>
            <div className="text-[10.5px] text-white/75 truncate">{isPt ? "Para" : "To"}: {email}</div>
          </div>
          <div className="flex rounded-md overflow-hidden border border-white/30 text-[10px] font-bold">
            {["pt", "en"].map((l) => (
              <button key={l} type="button" data-testid={`claire-draft-lang-${l}`}
                onClick={() => { setLang(l); generate(l); }}
                className={`px-2 py-1 uppercase ${lang === l ? "bg-white text-[#6D28D9]" : "text-white/80 hover:bg-white/10"}`}>
                {l}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} data-testid="claire-draft-close" className="ml-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? (
            <div className="py-10 text-center text-[12.5px] text-[var(--al-text-muted,#6B7280)]">
              <Loader2 className="w-5 h-5 animate-spin inline mb-2" />
              <div>{isPt ? "Claire está analisando o lead e redigindo..." : "Claire is analyzing the lead and writing..."}</div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10.5px] uppercase tracking-wide font-bold text-[var(--al-text-muted,#6B7280)]">
                  {isPt ? "Assunto sugerido" : "Suggested subject"}
                </label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  data-testid="claire-draft-subject"
                  className="mt-1 w-full h-9 px-3 rounded-lg border border-[var(--al-border,#E5E7EB)] bg-transparent text-[13px] font-semibold" />
              </div>
              <div>
                <label className="text-[10.5px] uppercase tracking-wide font-bold text-[var(--al-text-muted,#6B7280)]">
                  {isPt ? "Mensagem" : "Message"}
                </label>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8}
                  data-testid="claire-draft-body"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--al-border,#E5E7EB)] bg-transparent text-[12.5px] leading-relaxed resize-y" />
              </div>
            </>
          )}

          <div className="flex items-center gap-2 pt-1">
            <a href={mailtoHref} data-testid="claire-draft-open-mail"
              onClick={(e) => { if (loading) e.preventDefault(); }}
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12.5px] font-bold">
              <Send className="w-3.5 h-3.5" /> {isPt ? "Abrir no E-mail" : "Open in Email"}
            </a>
            <button type="button" data-testid="claire-draft-copy" disabled={loading}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`${subject}\n\n${body}`);
                  toast.success(isPt ? "Copiado!" : "Copied!");
                } catch (_e) { toast.error(isPt ? "Cópia bloqueada pelo navegador" : "Copy blocked by browser"); }
              }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--al-border,#E5E7EB)] text-[12px] font-semibold hover:bg-black/5 disabled:opacity-50">
              <Copy className="w-3.5 h-3.5" /> {isPt ? "Copiar" : "Copy"}
            </button>
            <button type="button" data-testid="claire-draft-regenerate" disabled={loading}
              onClick={() => generate(lang)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#8B5CF6] text-[#8B5CF6] text-[12px] font-semibold hover:bg-[#8B5CF6]/10 disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" /> {isPt ? "Regenerar" : "Regenerate"}
            </button>
          </div>
          <p className="text-[10px] text-[var(--al-text-muted,#6B7280)]">
            {isPt ? "Custa 1 crédito por rascunho. Edite livremente antes de enviar." : "1 credit per draft. Edit freely before sending."}
          </p>
        </div>
      </div>
    </div>
  );
}

export function EmailAction({ email, context, isPt, className = "", testid = "contact-email" }) {
  const [open, setOpen] = useState(false);
  if (!email) return null;
  if (!email.includes("@")) {
    return <span className={`truncate ${className}`} data-testid={`${testid}-masked`}>{email}</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}>
      <a href={`mailto:${email}`} data-testid={`${testid}-link`}
        onClick={(e) => e.stopPropagation()}
        className="truncate text-[#1B3A8A] hover:underline">{email}</a>
      <button type="button" data-testid={`${testid}-claire-btn`}
        title={isPt ? "Claire: sugerir assunto e mensagem" : "Claire: suggest subject & message"}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="shrink-0 w-6 h-6 rounded-md bg-[#8B5CF6]/10 text-[#8B5CF6] hover:bg-[#8B5CF6] hover:text-white flex items-center justify-center transition-colors">
        <Mail className="w-3.5 h-3.5" />
      </button>
      <ClaireEmailDraftModal open={open} onClose={() => setOpen(false)}
        email={email} context={context} isPt={isPt} />
    </span>
  );
}

export function PhoneAction({ phone, isPt, className = "", testid = "contact-phone" }) {
  if (!phone) return null;
  const tel = phone.replace(/[^+\d]/g, "");
  const wa = phone.replace(/[^\d]/g, "");
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <a href={`tel:${tel}`} data-testid={`${testid}-link`}
        onClick={(e) => e.stopPropagation()}
        className="hover:text-[#3B82F6] hover:underline inline-flex items-center gap-1">
        <Phone className="w-3 h-3 opacity-60" /> {phone}
      </a>
      <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
        data-testid={`${testid}-wa`} title={isPt ? "Abrir no WhatsApp" : "Open in WhatsApp"}
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 w-6 h-6 rounded-md bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-colors">
        <MessageCircle className="w-3.5 h-3.5" />
      </a>
    </span>
  );
}
